import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import { loadRecaptchaScript, getRecaptchaToken } from '../../../utils/recaptcha.js';
import HubAuthShell from './HubAuthShell.jsx';

/**
 * パスワードリセットページ
 */
const ResetPasswordPage = () => {
  const [clinicName, setClinicName] = useState('');
  const [userId, setUserId] = useState('');
  const [clinicEmail, setClinicEmail] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleClinicInputChange = (value) => {
    setClinicName(value);
    setSelectedClinicId('');
    setAmbiguousCandidates([]);
  };

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken('reset_password');
      const payload = {
        clinicName: clinicName.trim(),
        userId,
        email: clinicEmail,
        recaptchaToken,
      };
      if (selectedClinicId) {
        payload.clinicId = selectedClinicId;
      }

      const response = await fetch(buildApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 409 && data?.error === 'clinic-ambiguous') {
        setAmbiguousCandidates(Array.isArray(data.candidates) ? data.candidates : []);
        setError(data.message || '同名のクリニックが複数見つかりました。選択してください。');
        return;
      }

      if (!response.ok) {
        assertAuthApiOk(response, data, 'パスワードリセットのリクエストに失敗しました。');
        throw new Error(data.message || 'パスワードリセットのリクエストに失敗しました。');
      }

      setResetLink(data.resetLink || '');
      setSuccess(true);
    } catch (err) {
      console.error('パスワードリセットエラー:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const outsideHome = (
    <div style={{ textAlign: 'center', marginTop: 'var(--hub-space-xl)' }}>
      <Link
        to="/"
        style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.875rem',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        トップページに戻る
      </Link>
    </div>
  );

  if (success) {
    return (
      <HubAuthShell revealDeps={['success']}>
        <div className="hub-auth-header">
          <div className="hub-auth-logo" style={{ background: 'var(--hub-success)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className="hub-auth-title">リクエストを受け付けました</h1>
          <p className="hub-auth-subtitle">
            登録情報が一致する場合、パスワードリセット用のリンクを送信します
          </p>
        </div>

        <div style={{
          background: 'var(--hub-bg-secondary)',
          borderRadius: 'var(--hub-radius-md)',
          padding: 'var(--hub-space-lg)',
          marginBottom: 'var(--hub-space-xl)',
          textAlign: 'center',
        }}>
          <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
            クリニック名・ユーザーID・メールアドレスが登録情報と一致する場合、
            パスワードリセット用のリンクをメールでお送りします。メールをご確認ください。
          </p>
          {resetLink && (
            <p style={{ marginTop: 'var(--hub-space-md)', marginBottom: 0 }}>
              <a href={resetLink} className="hub-btn hub-btn-secondary" style={{ display: 'inline-block' }}>
                リセット用ページを開く
              </a>
            </p>
          )}
        </div>

        <Link to="/login" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
          ログインページに戻る
        </Link>
      </HubAuthShell>
    );
  }

  return (
    <HubAuthShell outside={outsideHome}>
      <div className="hub-auth-header">
        <div className="hub-auth-logo">
          <img src="/logo.webp" alt="Synapse" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        </div>
        <h1 className="hub-auth-title">パスワードをリセット</h1>
        <p className="hub-auth-subtitle">
          登録情報を入力してください
        </p>
      </div>

      {error && (
        <div className="hub-auth-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">クリニック名またはコード</label>
          <input
            type="text"
            className="hub-form-input"
            value={clinicName}
            onChange={(e) => handleClinicInputChange(e.target.value)}
            placeholder="クリニック名またはコード"
            required
          />
          <span className="hub-form-hint">
            クリニック名、または4文字のクリニックコードを入力してください
          </span>
        </div>

        {ambiguousCandidates.length > 0 && (
          <div className="hub-form-group">
            <label className="hub-form-label hub-form-required">クリニックを選択</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ambiguousCandidates.map((candidate) => (
                <button
                  key={candidate.clinicId}
                  type="button"
                  className={`hub-btn ${selectedClinicId === candidate.clinicId ? 'hub-btn-primary' : 'hub-btn-secondary'}`}
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => setSelectedClinicId(candidate.clinicId)}
                >
                  {candidate.name}{candidate.code ? ` (${candidate.code})` : ''}{candidate.address ? ` — ${candidate.address}` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">ユーザーID</label>
          <input
            type="text"
            className="hub-form-input"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="ユーザーID"
            required
          />
        </div>

        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">クリニックメールアドレス</label>
          <input
            type="email"
            className="hub-form-input"
            value={clinicEmail}
            onChange={(e) => setClinicEmail(e.target.value)}
            placeholder="クリニックの代表メールアドレス"
            required
          />
          <span className="hub-form-hint">
            クリニックに登録されているメールアドレスを入力してください
          </span>
        </div>

        <button
          type="submit"
          className="hub-btn hub-btn-primary"
          style={{ width: '100%' }}
          disabled={loading || (ambiguousCandidates.length > 0 && !selectedClinicId)}
        >
          {loading ? (
            <>
              <LoadingSpinner size="small" />
              送信中...
            </>
          ) : (
            'リセットリンクを送信'
          )}
        </button>
      </form>

      <div className="hub-auth-footer">
        <p>
          <Link to="/login">ログインページに戻る</Link>
        </p>
      </div>
    </HubAuthShell>
  );
};

export default ResetPasswordPage;
